import { tokenizePromptForHighlight } from './promptSyntax.js';
import { getNsfwEnabled } from '../utils/userPrefs.js';
import { getFilteredUnrecognizedTagTitle } from '../utils/nsfwTags.js';

function getSelectionStartInEditor(editor) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return 0;
  }

  const range = selection.getRangeAt(0);
  if (!editor.contains(range.endContainer)) {
    return 0;
  }

  const preRange = range.cloneRange();
  preRange.selectNodeContents(editor);
  preRange.setEnd(range.endContainer, range.endOffset);
  return preRange.toString().length;
}

function setSelectionInEditor(editor, index) {
  const safeIndex = Math.max(0, index);
  const selection = window.getSelection();
  if (!selection) {
    return;
  }

  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  let currentNode = walker.nextNode();
  let consumed = 0;

  while (currentNode) {
    const textLength = currentNode.textContent ? currentNode.textContent.length : 0;
    if (consumed + textLength >= safeIndex) {
      const offset = safeIndex - consumed;
      const range = document.createRange();
      range.setStart(currentNode, offset);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }

    consumed += textLength;
    currentNode = walker.nextNode();
  }

  const fallbackRange = document.createRange();
  fallbackRange.selectNodeContents(editor);
  fallbackRange.collapse(false);
  selection.removeAllRanges();
  selection.addRange(fallbackRange);
}

function appendHighlightedPart(fragment, part, isForbiddenTag, isUnrecognizedTag) {
  if (part.kind === 'plain') {
    fragment.appendChild(document.createTextNode(part.text));
    return;
  }

  const tagSpan = document.createElement('span');
  tagSpan.className = 'rich-prompt-editor-tag';
  const unrecognized = part.kind === 'unrecognized' || isUnrecognizedTag(part.text);
  if (part.kind !== 'unrecognized' && isForbiddenTag(part.text)) {
    tagSpan.classList.add('rich-prompt-editor-tag--forbidden');
  } else if (unrecognized) {
    tagSpan.classList.add('rich-prompt-editor-tag--unrecognized');
    if (!getNsfwEnabled()) {
      const title = getFilteredUnrecognizedTagTitle();
      tagSpan.title = title;
      tagSpan.setAttribute('aria-label', `${part.text}. ${title}`);
    }
  }
  tagSpan.textContent = part.text;
  fragment.appendChild(tagSpan);
}

function buildHighlightedFragment(text, isForbiddenTag, isUnrecognizedTag) {
  const fragment = document.createDocumentFragment();
  if (!text) return fragment;

  tokenizePromptForHighlight(text).forEach((part) => {
    appendHighlightedPart(fragment, part, isForbiddenTag, isUnrecognizedTag);
  });
  return fragment;
}

export function refreshRichPromptEditor(textarea) {
  if (!textarea || textarea.dataset.richPromptEditor !== 'true') return;
  // Reassignment runs the value setter, which re-tokenizes brace tokens.
  textarea.value = textarea.value;
}

export function syncRichPromptEditorDisabledState(textarea) {
  if (!textarea || textarea.dataset.richPromptEditor !== 'true') {
    return;
  }

  const editor = textarea.nextElementSibling;
  if (!editor || !editor.classList.contains('rich-prompt-editor')) {
    return;
  }

  const isDisabled = textarea.disabled;
  editor.contentEditable = isDisabled ? 'false' : 'true';
  editor.setAttribute('aria-disabled', String(isDisabled));
  editor.tabIndex = isDisabled ? -1 : (textarea.dataset.richPromptEditorTabindex
    ? parseInt(textarea.dataset.richPromptEditorTabindex, 10)
    : 0);
}

export function initializeRichPromptEditor(textarea, options = {}) {
  if (!textarea || textarea.dataset.richPromptEditor === 'true') {
    return null;
  }

  const isForbiddenTag = options.isForbiddenTag || (() => false);
  const isUnrecognizedTag = options.isUnrecognizedTag || (() => false);
  const valueDescriptor = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value');
  if (!valueDescriptor || !valueDescriptor.get || !valueDescriptor.set) {
    return null;
  }

  const editor = document.createElement('div');
  editor.className = `${textarea.className || ''} rich-prompt-editor`;
  editor.setAttribute('contenteditable', 'true');
  editor.setAttribute('role', 'textbox');
  editor.setAttribute('aria-multiline', 'true');
  editor.setAttribute('data-placeholder', textarea.placeholder || '');
  editor.spellcheck = false;

  const ariaLabel = textarea.getAttribute('aria-label');
  if (ariaLabel) {
    editor.setAttribute('aria-label', ariaLabel);
  }

  if (textarea.tabIndex >= 0) {
    editor.tabIndex = textarea.tabIndex;
    textarea.dataset.richPromptEditorTabindex = String(textarea.tabIndex);
  } else {
    editor.tabIndex = 0;
    textarea.dataset.richPromptEditorTabindex = '0';
  }

  textarea.insertAdjacentElement('afterend', editor);
  textarea.classList.add('rich-prompt-editor-source');
  textarea.dataset.richPromptEditor = 'true';
  textarea.setAttribute('aria-hidden', 'true');
  textarea.tabIndex = -1;

  let isSyncing = false;

  const renderEditorValue = (text, caretIndex = null) => {
    const fragment = buildHighlightedFragment(text, isForbiddenTag, isUnrecognizedTag);
    editor.replaceChildren(fragment);
    if (typeof caretIndex === 'number') {
      setSelectionInEditor(editor, caretIndex);
    }
  };

  const syncToTextarea = (nextValue, caretIndex, shouldDispatchInput) => {
    const nextText = typeof nextValue === 'string' ? nextValue : '';
    isSyncing = true;
    valueDescriptor.set.call(textarea, nextText);
    if (typeof caretIndex === 'number' && typeof textarea.setSelectionRange === 'function') {
      textarea.setSelectionRange(caretIndex, caretIndex);
    }
    isSyncing = false;

    renderEditorValue(nextText, caretIndex);

    if (shouldDispatchInput) {
      textarea.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  Object.defineProperty(textarea, 'value', {
    configurable: true,
    enumerable: true,
    get() {
      return valueDescriptor.get.call(this);
    },
    set(nextValue) {
      valueDescriptor.set.call(this, nextValue);
      if (isSyncing) {
        return;
      }
      renderEditorValue(typeof nextValue === 'string' ? nextValue : '');
    }
  });

  renderEditorValue(textarea.value || '');
  syncRichPromptEditorDisabledState(textarea);

  editor.addEventListener('input', () => {
    const caretIndex = getSelectionStartInEditor(editor);
    syncToTextarea(editor.textContent || '', caretIndex, true);
  });

  editor.addEventListener('paste', (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData ? event.clipboardData.getData('text/plain') : '';
    const currentValue = textarea.value || '';
    const selectionStart = getSelectionStartInEditor(editor);
    const selectionEnd = selectionStart;
    const nextValue = `${currentValue.slice(0, selectionStart)}${pastedText}${currentValue.slice(selectionEnd)}`;
    const nextCaret = selectionStart + pastedText.length;
    syncToTextarea(nextValue, nextCaret, true);
  });

  return {
    editor,
    getValue: () => textarea.value || '',
    setValue: (nextValue, caretIndex, shouldDispatchInput = true) => {
      const normalized = typeof nextValue === 'string' ? nextValue : '';
      const safeCaret = typeof caretIndex === 'number' ? caretIndex : normalized.length;
      syncToTextarea(normalized, safeCaret, shouldDispatchInput);
    },
    getSelectionStart: () => getSelectionStartInEditor(editor),
    setSelectionRange: (selectionStart) => setSelectionInEditor(editor, selectionStart),
    focus: () => editor.focus(),
    containsTarget: (target) => target === editor || editor.contains(target)
  };
}
